import { toast } from "sonner";
import {
  buildCoachPlanFromProgramRunDay,
  formatRunningWorkoutForCoach,
  isRunningProgramExercise,
  runningWorkoutPlanFromProgramDay,
} from "@/utils/workoutImporter";
import { appendArnoldRunPlans } from "@/lib/runningCoachPlans";
import { structureRunGuideForCoach } from "@/lib/runGuideStructure";
import { normalizeCoachPlanText } from "@/lib/runningCoach";

export type ProgramRunDayLike = {
  workout_title: string;
  workout_description?: string | null;
  estimated_duration_minutes?: number | null;
  day_name?: string | null;
  day_number?: number | null;
  exercises: Array<{
    exercise_name: string;
    notes?: string | null;
    coach_plan_lines?: string[] | null;
    sets?: number | null;
    reps_min?: number | null;
    reps_max?: number | null;
  }>;
};

/** True when a program day is an outdoor/GPS run (not gym sets×reps). */
export function isProgramDayRunning(exercises: Array<{ exercise_name?: string | null }> | null | undefined): boolean {
  if (!exercises?.length) return false;
  const runExercises = exercises.filter((ex) => isRunningProgramExercise(ex.exercise_name || ""));
  if (runExercises.length === 0) return false;
  // Single run exercise, or every exercise is a run variant
  if (exercises.length === 1) return true;
  return runExercises.length === exercises.length;
}

export type LaunchProgramRunResult = {
  planText: string;
  /** Freemium: plan is loaded but live voice coach stays off until upgrade. */
  coachEnabled: boolean;
};

/**
 * Prepare a program run day for `/running-start` (plan text + localStorage).
 * Caller navigates with the returned planText.
 */
export async function prepareProgramRunLaunch(
  day: ProgramRunDayLike,
  opts?: { enableCoach?: boolean },
): Promise<LaunchProgramRunResult | null> {
  const exercise =
    day.exercises.find((ex) => isRunningProgramExercise(ex.exercise_name)) || day.exercises[0];
  if (!exercise) return null;

  const plan = runningWorkoutPlanFromProgramDay(day, exercise);
  let planText = "";
  try {
    planText = formatRunningWorkoutForCoach(plan);
  } catch {
    planText = buildCoachPlanFromProgramRunDay(day, exercise).join("\n");
  }

  planText = normalizeCoachPlanText(planText).trim();
  if (!planText) return null;

  // Only restructure vague free-text. Keep verbatim coach_plan_lines from Arnold.
  const alreadyPhased = planText.split("\n").filter(Boolean).length >= 2;
  if (!alreadyPhased) {
    try {
      const structured = await structureRunGuideForCoach(planText);
      if (structured?.plan?.trim()) {
        planText = structured.plan.trim();
      }
    } catch {
      /* keep original */
    }
  }

  try {
    appendArnoldRunPlans([
      {
        plan: {
          ...plan,
          coach_plan_lines: planText.split("\n").filter(Boolean),
        },
        suggestedAt: new Date().toISOString(),
      },
    ]);
  } catch {
    /* non-fatal */
  }

  const enableCoach = opts?.enableCoach === true;
  try {
    const currentSettings = JSON.parse(localStorage.getItem("runningUserSettings") || "{}");
    localStorage.setItem(
      "runningUserSettings",
      JSON.stringify({
        ...currentSettings,
        activityType: currentSettings.activityType || "outdoor",
        audioStats: currentSettings.audioStats || "5min",
        audioVoice: currentSettings.audioVoice || "female",
        coachGuide: {
          enabled: enableCoach,
          frequency: currentSettings.coachGuide?.frequency || "when_needed",
          plan: planText,
          source: "arnold",
          planStructured: true,
        },
      }),
    );
  } catch {
    /* non-fatal */
  }

  return { planText, coachEnabled: enableCoach };
}

export function toastOpeningRun(message?: string) {
  toast.success(message || "Opening AI Run Coach…");
}
